import { apiClient } from './apiClient';
import { useTenantStore } from '@/stores/useTenantStore';

const API_BASE = '/blog-creation';

export type EditorAIAction =
  | 'selection_edit'
  | 'ask'
  | 'inline_suggestions'
  | 'image_opportunities'
  | 'meta_suggestions'
  | 'internal_links'
  | 'social_repurpose'
  | 'structure_suggestions';

export interface StructureSuggestion {
  tag: 'H1' | 'H2' | 'H3';
  title: string;
}

export interface EditorAIRequest {
  action: EditorAIAction;
  prompt?: string;
  selected_text?: string;
  content_html?: string;
  content_json?: unknown;
  inputs?: Record<string, unknown>;
  force_refresh?: boolean;
}

export interface EditorAIResponse<T = Record<string, unknown>> {
  action: EditorAIAction;
  result: T;
  cached: boolean;
  provider?: string | null;
  model?: string | null;
  cache_key?: string | null;
}

export interface EditorSnapshot {
  html?: string;
  json?: unknown;
}

export interface EditorInlineSuggestion {
  id: string;
  kind: 'tone' | 'fact' | 'clarity' | 'seo';
  text: string;
  fix: string;
  reason?: string;
}

export interface EditorImageOpportunity {
  id?: string | number;
  placement?: string;
  image_type?: 'featured' | 'section' | 'diagram' | 'comparison' | 'process' | string;
  prompt?: string;
  alt_text?: string;
  rationale?: string;
  section?: string;
}

export interface GeneratedHeroImage {
  image_url: string;
  alt_text: string;
  prompt_used: string;
  featured_image_id?: number | null;
  media_id?: number | null;
  wordpress_media_id?: number | null;
  /** True when the image is a Supabase placeholder, not yet pushed to WordPress. */
  placeholder?: boolean;
}

export interface KeywordSuggestionsResult {
  keyword: string;
  suggestions: string[];
}

// No fallback to the AgileCo test tenant: a missing client selection is an
// explicit error at request time, not a silent switch to test data.
const getTenantId = (): string => {
  const tenantId = useTenantStore.getState().currentTenant;
  if (!tenantId) throw new Error('No tenant selected');
  return tenantId;
};

const tenantQuery = (tenantId?: string) =>
  encodeURIComponent(tenantId || getTenantId());

const selectionPrompt: Record<string, string> = {
  improve: 'Improve the selected text while preserving the meaning and voice.',
  shorten: 'Shorten the selected text while preserving the core meaning.',
  rewrite: 'Rewrite the selected text in a clearer editorial style.',
  simplify: 'Simplify the selected text for a homeowner audience.',
  proofread: 'Proofread the selected text and fix grammar, spelling, and punctuation only.',
};

export const editorAIService = {
  async run<T = Record<string, unknown>>(
    contentId: string,
    request: EditorAIRequest,
  ): Promise<EditorAIResponse<T>> {
    const response = await apiClient.post<EditorAIResponse<T>>(
      `${API_BASE}/content/${contentId}/editor-ai?tenant_id=${tenantQuery()}`,
      request,
      { timeout: 130_000 },
    );
    return response.data;
  },

  async updateContentMeta(
    contentId: string,
    meta: { meta_title: string; meta_description: string },
    tenantId?: string,
  ): Promise<void> {
    await apiClient.patch(
      `${API_BASE}/content/${contentId}?tenant_id=${tenantQuery(tenantId)}`,
      {
        meta_title: meta.meta_title,
        meta_description: meta.meta_description,
      },
    );
  },

  async updateSchemaTypes(
    contentId: string,
    schemaTypes: string[],
    tenantId?: string,
  ): Promise<void> {
    await apiClient.patch(
      `${API_BASE}/content/${contentId}?tenant_id=${tenantQuery(tenantId)}`,
      { schema_types_injected: schemaTypes },
    );
  },

  async updateFeaturedImageId(
    contentId: string,
    featuredImageId: number,
    tenantId?: string,
  ): Promise<void> {
    await apiClient.patch(
      `${API_BASE}/content/${contentId}?tenant_id=${tenantQuery(tenantId)}`,
      { featured_image_id: featuredImageId },
    );
  },

  async editSelection(
    contentId: string,
    params: {
      actionId: string;
      selectedText: string;
      customQuery?: string;
      snapshot?: EditorSnapshot;
    },
  ): Promise<EditorAIResponse<{ edited_text: string; original_text: string }>> {
    const prompt = params.customQuery || selectionPrompt[params.actionId] || 'Improve the selected text.';
    return this.run(contentId, {
      action: 'selection_edit',
      prompt,
      selected_text: params.selectedText,
      content_html: params.snapshot?.html,
      content_json: params.snapshot?.json,
      inputs: {
        action_id: params.actionId,
        custom_query: params.customQuery,
      },
    });
  },

  async askArticle(
    contentId: string,
    prompt: string,
    snapshot?: EditorSnapshot,
  ): Promise<EditorAIResponse<{ answer: string }>> {
    return this.run(contentId, {
      action: 'ask',
      prompt,
      content_html: snapshot?.html,
      content_json: snapshot?.json,
    });
  },

  async generateInlineSuggestions(
    contentId: string,
    snapshot?: EditorSnapshot,
  ): Promise<EditorAIResponse<{ suggestions: EditorInlineSuggestion[]; raw?: string }>> {
    return this.run(contentId, {
      action: 'inline_suggestions',
      prompt: 'Find inline improvements that should be shown as reviewable suggestions.',
      content_html: snapshot?.html,
      content_json: snapshot?.json,
    });
  },

  async getStructureSuggestions(
    contentId: string,
    inputs: {
      existing_headings: string[];
      focus_keyword: string;
    },
  ): Promise<EditorAIResponse<{ suggestions: StructureSuggestion[]; raw?: string }>> {
    return this.run(contentId, {
      action: 'structure_suggestions',
      inputs,
    });
  },

  async getImageOpportunities(
    contentId: string,
    snapshot?: EditorSnapshot,
    inputs?: Record<string, unknown>,
  ): Promise<EditorAIResponse<{ opportunities: EditorImageOpportunity[]; raw?: string }>> {
    return this.run(contentId, {
      action: 'image_opportunities',
      prompt: 'Find sections that would benefit from generated imagery.',
      content_html: snapshot?.html,
      content_json: snapshot?.json,
      inputs,
    });
  },

  async generateHeroImage(
    params: {
      title: string;
      keyword: string;
      style?: 'modern' | 'minimalist' | 'vibrant' | 'professional';
      contentId?: string;
      /**
       * Explicit image prompt. When provided the backend uses it verbatim and
       * skips title/keyword prompt-building. Used by the editor's inline
       * "Regenerate with AI" flow so the user's description drives the image.
       */
      prompt?: string;
    },
    tenantId?: string,
  ): Promise<GeneratedHeroImage> {
    const response = await apiClient.post<GeneratedHeroImage>(
      `${API_BASE}/content/generate-hero-image?tenant_id=${tenantQuery(tenantId)}`,
      {
        title: params.title,
        keyword: params.keyword,
        style: params.style ?? 'professional',
        content_id: params.contentId,
        prompt: params.prompt,
      },
      { timeout: 130_000 },
    );
    return response.data;
  },

  /** Inline hover suggestions for a highlighted keyword. Best-effort. */
  async getKeywordSuggestions(
    params: { keyword: string; context?: string; title?: string },
    tenantId?: string,
  ): Promise<KeywordSuggestionsResult> {
    const response = await apiClient.post<KeywordSuggestionsResult>(
      `${API_BASE}/content/keyword-suggestions?tenant_id=${tenantQuery(tenantId)}`,
      {
        keyword: params.keyword,
        context: params.context,
        title: params.title,
      },
      { timeout: 30_000 },
    );
    return response.data;
  },
};
